// Utilitários para cálculo e formatação de preços.
//
// Modelo de pricing (alinhado com a Documentação Funcional v1.0):
// - price: preço cheio para CONSUMER (preço normal de venda).
// - resellerPrice: preço para RESELLER, calculado como
//     resellerPrice = price * (1 - commissionPercent / 100)
// - cost: custo interno (uso administrativo). Trava de prejuízo:
//     resellerPrice deve ser >= cost.
// - commissionPercent: percentual de desconto aplicado ao RESELLER (0–100).

/**
 * Calcula o resellerPrice a partir de price e commissionPercent.
 * @param {number} price
 * @param {number} commissionPercent
 * @returns {number}
 */
export const calculateResellerPrice = (price, commissionPercent) => {
  const p = Number(price) || 0;
  const pct = Number(commissionPercent) || 0;
  return Math.round(p * (1 - pct / 100) * 100) / 100;
};

/**
 * Calcula o desconto unitário recebido pelo RESELLER.
 * @param {number} price
 * @param {number} resellerPrice
 * @returns {number}
 */
export const calculateDiscount = (price, resellerPrice) => {
  return Math.round((Number(price) - Number(resellerPrice)) * 100) / 100;
};

/**
 * Verifica se o preço de revenda gera prejuízo (trava client-side).
 * Espelha a validação do backend (validatePricing).
 * @param {{price:number, commissionPercent:number, cost:number}} input
 * @returns {{ ok: boolean, resellerPrice: number, message?: string }}
 */
export const checkPricingViability = ({ price, commissionPercent, cost }) => {
  const resellerPrice = calculateResellerPrice(price, commissionPercent);
  const c = Number(cost) || 0;
  if (c > 0 && resellerPrice < c) {
    return {
      ok: false,
      resellerPrice,
      message: `Preço revendedor (${formatPrice(resellerPrice)}) é menor que o custo (${formatPrice(c)}). Salvar resultaria em prejuízo.`
    };
  }
  return { ok: true, resellerPrice };
};

/**
 * Formata um valor monetário em BRL.
 * @param {number} value
 * @returns {string}
 */
export const formatPrice = (value) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(Number(value) || 0);
};

/**
 * Soma o total de um array de itens.
 * @param {Array} items
 * @param {string} priceField
 * @returns {number}
 */
export const calculateTotal = (items, priceField = 'price') => {
  return items.reduce((sum, item) => {
    const v = Number(item[priceField]) || 0;
    const q = Number(item.quantity) || 0;
    return sum + v * q;
  }, 0);
};

/**
 * Resolve o preço aplicável a um item conforme o role do usuário e o businessType da loja.
 * - RESELLER em store RESELLER: usa resellerPrice (se disponível).
 * - Caso contrário: usa price.
 *
 * Retorna sempre um number.
 * @param {{ price?:number, resellerPrice?:number }} variationOrProduct
 * @param {{ roleInStore?:string, businessType?:string }} context
 * @returns {number}
 */
export const resolveApplicablePrice = (variationOrProduct, { roleInStore, businessType } = {}) => {
  if (!variationOrProduct) return 0;
  const price = Number(variationOrProduct.price) || 0;
  const reseller = variationOrProduct.resellerPrice != null ? Number(variationOrProduct.resellerPrice) : null;
  const isResellerView = roleInStore === 'reseller' && businessType === 'reseller';
  if (isResellerView && reseller != null && reseller > 0) {
    return reseller;
  }
  return price;
};

/**
 * Indica se uma variação/produto tem preço de revenda exibível.
 *
 * O backend (`serializeProductForRole`) só envia `resellerPrice` quando o
 * requester é RESELLER numa store RESELLER (ou MANAGER/REVITER). A UI usa
 * a presença do campo como gate — mais robusto que depender de
 * `isResellerInCurrentStore`, que quebra se o `currentStoreId` não estiver
 * sincronizado com o storefront sendo visitado.
 * @param {{ price?:number, resellerPrice?:number }} v
 * @returns {boolean}
 */
export const hasResellerPricing = (v) =>
  !!v
  && v.resellerPrice != null
  && Number(v.resellerPrice) > 0
  && Number(v.resellerPrice) < Number(v.price);

// ====== Compat shims ======
// Manter para não quebrar callers antigos que ainda importam por nome.
// TODO Fase 5/9: remover usos de calculatePriceToPay/calculateCommissionFromPercent.

/**
 * @deprecated Use `calculateDiscount` / `resolveApplicablePrice`.
 */
export const calculatePriceToPay = (resalePrice, commission) => {
  return Number(resalePrice) - Number(commission);
};

/**
 * @deprecated Renomeado para `calculateResellerPrice` (calcula price - desconto, não comissão sobre revenda).
 */
export const calculateCommissionFromPercent = (price, commissionPercent) => {
  return (Number(price) * Number(commissionPercent)) / 100;
};
